<script lang="ts">
import type { ButtonProps } from '#layers/ui/components/Button.vue';
import type { IconFrameProps } from '#layers/ui/components/IconFrame.vue';
import { DEFAULT_COLORS } from '#layers/ui/utils/colors';

export interface IconPickerExtraTab {
	value: string;
	label: string;
	testId?: string;
	active?: boolean;
}

export interface IconPickerProps {
	pickColor?: boolean;
	pickEmoji?: boolean;
	placeholderIcon?: string;
	defaultColor?: string;
	block?: boolean;
	size?: ButtonProps['size'];
	variant?: IconFrameProps['variant'];
	spacing?: IconFrameProps['spacing'];
	colorPresets?: string[];
	clear?: boolean;
	readonly?: boolean;
	disabled?: boolean;
	/** Overrides the internal icon state for clear controls. */
	hasValue?: boolean | undefined;
	/** Wrapper-provided tab rendered through the `extra-tab` slot. */
	extraTab?: IconPickerExtraTab;
}
</script>

<script lang="ts" setup>
import { computed, defineComponent, nextTick, ref, useTemplateRef, watch } from 'vue';
import { isNil } from '#layers/base/public';
import { useFormField } from '#layers/ui/composables/use-form-field';
import { EMOJI_PREFIX, emojiShortcode, isEmojiName } from '#layers/ui/utils/emoji-glyph';
import IconPickerTabEmoji from './IconPickerTabEmoji.vue';
import IconPickerTabIcon from './IconPickerTabIcon.vue';

const props = withDefaults(defineProps<IconPickerProps>(), {
	colorPresets: () => [...DEFAULT_COLORS],
	variant: 'link',
	spacing: 'comfortable',
	clear: true,
	hasValue: undefined,
});

const emit = defineEmits<{
	clear: [];
}>();
const icon = defineModel<string | null>();
const color = defineModel<string | null>('color');

const openPicker = ref(false);
const { size: formGroupSize } = useFormField<IconPickerProps>(props);

const IconPickerTabKeys = {
	Icon: 'icon',
	Emoji: 'emoji',
	CurrentEmoji: 'current-emoji',
} as const;

const effectiveSize = computed(() => props.size ?? formGroupSize.value ?? 'md');

const triggerSizeClass = computed(() => {
	const sizes: Record<string, string> = { xs: 'size-8', sm: 'size-8', md: 'size-10', lg: 'size-12', xl: 'size-13' };
	return sizes[effectiveSize.value] ?? 'size-8';
});

const isEmoji = computed(() => isEmojiName(icon.value));
const hasIcon = computed(() => !isNil(icon.value));

const effectiveHasValue = computed(() => props.hasValue ?? hasIcon.value);

// Keep a stored emoji visible when emoji input is disabled.
const showCurrentEmojiTab = computed(() => isEmoji.value && !props.pickEmoji);

const showTabs = computed(() => Boolean(props.pickEmoji || props.extraTab || showCurrentEmojiTab.value));

function computeActiveTab(): string {
	if (props.extraTab?.active) return props.extraTab.value;
	if (isEmoji.value && props.pickEmoji) return IconPickerTabKeys.Emoji;
	if (showCurrentEmojiTab.value) return IconPickerTabKeys.CurrentEmoji;
	return IconPickerTabKeys.Icon;
}

const activeTab = ref<string>(computeActiveTab());

// Preserve manual tab changes while the popover is open.
watch([isEmoji, () => props.extraTab?.active, () => openPicker.value], () => {
	if (!openPicker.value) return;
	activeTab.value = computeActiveTab();
});

interface IconPickerTabItem {
	value: string;
	label: string;
	testId: string;
	active: boolean;
	disabled?: boolean;
}

const tabItems = computed<IconPickerTabItem[]>(() => {
	const items: IconPickerTabItem[] = [
		{ value: IconPickerTabKeys.Icon, label: 'icon_picker.tab_icon', testId: 'icon-picker-tab-item-icon', active: hasIcon.value && !isEmoji.value },
	];

	if (props.pickEmoji) {
		items.push({ value: IconPickerTabKeys.Emoji, label: 'icon_picker.tab_emoji', testId: 'icon-picker-tab-item-emoji', active: isEmoji.value });
	}
	else if (showCurrentEmojiTab.value) {
		items.push({
			value: IconPickerTabKeys.CurrentEmoji,
			label: 'icon_picker.tab_emoji',
			testId: 'icon-picker-tab-item-current-emoji',
			active: true,
			disabled: true,
		});
	}

	if (props.extraTab) {
		items.push({
			value: props.extraTab.value,
			label: props.extraTab.label,
			testId: props.extraTab.testId ?? `icon-picker-tab-item-${props.extraTab.value}`,
			active: Boolean(props.extraTab.active),
		});
	}

	return items;
});

const currentEmojiShortcode = computed(() => {
	if (!isEmoji.value || !icon.value) return '';
	return emojiShortcode(icon.value);
});

const displayedIcon = computed(() => icon.value || props.placeholderIcon);
const effectiveColor = computed(() => color.value || props.defaultColor || 'var(--ui-text)');
const selectedColorPreset = computed(() => color.value ?? props.defaultColor ?? null);
const hasColor = computed(() => !isNil(color.value));
const triggerVariant = computed<IconFrameProps['variant']>(() => hasColor.value ? 'soft' : props.variant);

watch(openPicker, (val) => {
	if (val) {
		focusActiveTabSearchInput();
	}
});

function selectIcon(name: string) {
	icon.value = name;
	if (!props.pickColor) openPicker.value = false;
}

function selectEmoji(shortcode: string) {
	icon.value = `${EMOJI_PREFIX}${shortcode}`;
	if (!props.pickColor) openPicker.value = false;
}

function clearSelection() {
	icon.value = null;
	emit('clear');
}

function clearColorOnly() {
	color.value = null;
}

const showFooterClearColor = computed(() => Boolean(props.pickColor) && hasColor.value);

const blockText = computed(() => {
	if (isEmoji.value && icon.value) return icon.value.slice(EMOJI_PREFIX.length);
	return icon.value;
});

interface IconPickerSearchTab {
	focusSearchInput: () => Promise<void>;
}

const iconTab = useTemplateRef<IconPickerSearchTab>('iconTab');

// Scope dismissal protection to this picker's tone popover.
const emojiTab = useTemplateRef<IconPickerSearchTab & {
	isTonePopoverOpen: boolean;
	tonePopoverPanel: HTMLElement | null;
}>('emojiTab');

async function waitForTabTriggerFocus() {
	await nextTick();
	await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
}

async function focusActiveTabSearchInput() {
	await waitForTabTriggerFocus();

	if (activeTab.value === IconPickerTabKeys.Icon) {
		await iconTab.value?.focusSearchInput();
		return;
	}

	if (activeTab.value === IconPickerTabKeys.Emoji) {
		await emojiTab.value?.focusSearchInput();
	}
}

watch(activeTab, () => focusActiveTabSearchInput());

function isInsideOwnTonePopover(target: EventTarget | null): boolean {
	if (!emojiTab.value?.isTonePopoverOpen) return false;
	if (!(target instanceof Node)) return false;
	const panel = emojiTab.value.tonePopoverPanel;
	return Boolean(panel?.contains(target));
}

// The teleported tone popover must not dismiss its parent.
const popoverContent = {
	align: 'start' as const,
	onPointerDownOutside(event: { target: EventTarget | null; preventDefault: () => void }) {
		if (isInsideOwnTonePopover(event.target)) event.preventDefault();
	},
	onFocusOutside(event: { target: EventTarget | null; preventDefault: () => void }) {
		if (isInsideOwnTonePopover(event.target)) event.preventDefault();
	},
};

// Block mode needs a positioned wrapper for the clear button.
const IconPickerRoot = defineComponent({
	name: 'IconPickerRoot',
	inheritAttrs: false,
	setup: (_, { slots }) => () => slots.default?.(),
});
</script>

<template>
	<component :is="block ? 'div' : IconPickerRoot" :class="block ? 'relative' : undefined">
		<DPopover
			v-model:open="openPicker"
			:content="popoverContent"
			v-bind="$attrs"
		>
			<slot>
				<DButton
					v-if="block"
					color="neutral"
					variant="outline"
					:trailing-icon="!readonly ? 'lucide:chevron-down' : undefined"
					block
					class="items-center overflow-hidden font-normal"
					:class="{ 'pointer-events-none': readonly }"
					:size="effectiveSize"
					:aria-label="$t('icon_picker.select_icon')"
					:disabled
					:readonly
				>
					<slot
						name="preview"
						mode="block"
						:displayed-icon
						:effective-color
						:has-color
						:variant="hasColor ? 'soft' : 'link'"
						spacing="tight"
					>
						<DIconFrame
							v-if="displayedIcon"
							:name="displayedIcon"
							:color="effectiveColor"
							:variant="hasColor ? 'soft' : 'link'"
							spacing="tight"
							class="size-4"
							:disabled
						/>
						<div v-else class="size-4" />
					</slot>

					<div class="mr-auto flex-1 truncate" :class="{ 'pe-8': hasIcon && !readonly }">
						{{ blockText }}
					</div>
				</DButton>

				<DButton
					v-else
					color="neutral"
					variant="outline"
					class="
						inline-flex aspect-square w-min! cursor-pointer items-center justify-center overflow-clip p-0! font-normal
						ring-default
					"
					:class="[triggerSizeClass, { 'pointer-events-none': readonly }]"
					:aria-label="$t('icon_picker.select_icon')"
					:readonly
					:disabled
				>
					<slot
						name="preview"
						mode="trigger"
						:displayed-icon
						:effective-color
						:has-color
						:variant="triggerVariant"
						:spacing
					>
						<DIconFrame
							v-if="displayedIcon"
							:name="displayedIcon"
							:color="effectiveColor"
							:variant="triggerVariant"
							:spacing
							:disabled
							class="aspect-square h-full"
						/>
						<DIcon v-else name="lucide:plus" class="size-5 text-dimmed" />
					</slot>
				</DButton>
			</slot>

			<template #content>
				<div
					class="
						flex max-h-(--reka-popper-available-height) w-105 max-w-[calc(100vw-2rem)] flex-col divide-y divide-default
						overflow-hidden
					"
				>
					<DTabs
						v-if="showTabs"
						v-model="activeTab"
						:items="tabItems"
						:content="false"
						variant="link"
						size="sm"
						data-testid="icon-picker-tabs"
						class="px-2 pt-1"
					>
						<template #default="{ item }">
							<span :data-testid="item.testId" class="inline-flex items-center gap-1.5">
								{{ $t(item.label) }}
								<span
									v-if="item.active"
									data-testid="icon-picker-tab-dot"
									class="inline-block size-1.5 rounded-full bg-primary"
								/>
							</span>
						</template>
					</DTabs>

					<div v-show="activeTab === IconPickerTabKeys.Icon" class="flex h-81 min-h-0 flex-col">
						<IconPickerTabIcon
							ref="iconTab"
							v-model="icon"
							:effective-color
							:readonly
							:disabled
							@select="selectIcon"
						/>
					</div>

					<div v-if="pickEmoji" v-show="activeTab === IconPickerTabKeys.Emoji" class="flex h-81 min-h-0 flex-col">
						<IconPickerTabEmoji
							ref="emojiTab"
							:selected-shortcode="currentEmojiShortcode"
							:readonly
							:disabled
							@select="selectEmoji"
						/>
					</div>

					<div
						v-if="showCurrentEmojiTab"
						v-show="activeTab === IconPickerTabKeys.CurrentEmoji"
						data-testid="icon-picker-current-emoji-panel"
						class="flex h-81 min-h-0 flex-col items-center justify-center gap-3 px-6 py-8 text-center"
					>
						<DIconFrame
							v-if="icon"
							:name="icon"
							:color="effectiveColor"
							variant="link"
							spacing="comfortable"
							class="size-16"
						/>
						<div class="font-mono text-sm text-toned">
							{{ currentEmojiShortcode }}
						</div>
						<p class="max-w-72 text-xs text-dimmed">
							{{ $t('icon_picker.current_emoji_caption') }}
						</p>
						<DButton
							v-if="!readonly"
							data-testid="icon-picker-current-emoji-clear"
							variant="ghost"
							color="neutral"
							size="sm"
							class="font-normal"
							@click="clearSelection"
						>
							{{ $t('icon_picker.current_emoji_clear') }}
						</DButton>
					</div>

					<div v-if="extraTab" v-show="activeTab === extraTab.value" class="flex h-81 min-h-0 flex-col">
						<slot name="extra-tab" />
					</div>

					<div v-if="pickColor" class="p-3 px-4" data-testid="color-swatches">
						<DColorPresetPicker
							:model-value="selectedColorPreset"
							:presets="colorPresets"
							:default-custom-color="defaultColor ?? colorPresets[0]"
							allow-custom
							@update:model-value="color = $event"
						/>
					</div>

					<div
						v-if="clear && !readonly && (effectiveHasValue || showFooterClearColor)"
						class="flex items-center justify-between p-2"
					>
						<DButton
							v-if="showFooterClearColor"
							data-testid="clear-color"
							variant="ghost"
							color="neutral"
							size="sm"
							class="font-normal"
							@click="clearColorOnly"
						>
							{{ $t('icon_picker.clear_color') }}
						</DButton>
						<span v-else />

						<DButton
							v-if="effectiveHasValue"
							data-testid="icon-picker-clear"
							variant="ghost"
							color="neutral"
							size="sm"
							class="font-normal"
							@click="clearSelection"
						>
							{{ $t('common.remove') }}
						</DButton>
					</div>
				</div>
			</template>
		</DPopover>
		<DButton
			v-if="block && clear && !readonly && effectiveHasValue"
			icon="lucide:x"
			variant="ghost"
			color="neutral"
			:size="effectiveSize"
			:ui="{ base: 'absolute inset-e-8 top-1/2 z-1 -translate-y-1/2 opacity-50 hover:bg-accented hover:opacity-100' }"
			:aria-label="$t('common.remove')"
			:disabled
			@click.stop.prevent="clearSelection"
		/>
	</component>
</template>
